import { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { WebhookService, WebhookSubscription, isChatFormat } from '../services/webhook.service';
import { WebhookDialog } from './webhook-dialog';
import { WebhookRow } from './webhook-row';

/// Endereços de sistema que recebem os eventos do CRM em JSON assinado; os de chat têm tela própria.
export const WebhookList = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert, alertDelete } = useAlert();

    const [list, setList] = useState<WebhookSubscription[] | null>(null);
    const [edit, setEdit] = useState<WebhookSubscription | null>(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(() => {
        new WebhookService()
            .list()
            .then((res) => setList((res.data.data ?? []).filter((item) => !isChatFormat(item.format))))
            .catch(() => setList([]));
    }, []);

    useEffect(load, [load]);

    const remove = (item: WebhookSubscription) =>
        alertDelete(item.id, async () => {
            try {
                await new WebhookService().deleteById(item.id);
                showAlert(t('general.exclusaoSucesso'), 'success', 2500);
                load();
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        }, item.name);

    return (
        // minmax(0, 1fr): sem ele a coluna cresce até a URL inteira e o cartão vaza para o lado.
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="caption" sx={{ flex: 1, color: cc.textSecondary }}>
                    {t('webhooks.ajuda')}
                </Typography>

                {/* Lista vazia já traz o botão no meio; repetir no topo seria ruído. */}
                {list && list.length > 0 && (
                    <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setCreating(true)}
                        sx={{ flexShrink: 0 }}
                    >
                        {t('webhooks.adicionar')}
                    </MDButton>
                )}
            </Box>

            {!list ? (
                <Skeleton variant="rounded" height={88} />
            ) : list.length === 0 ? (
                <Box
                    sx={{
                        display: 'grid',
                        justifyItems: 'center',
                        gap: 1.5,
                        py: 4,
                        border: `1px dashed ${cc.borderStrong}`,
                        borderRadius: '10px',
                    }}
                >
                    <Typography sx={{ fontSize: 13, color: cc.textMuted }}>
                        {t('webhooks.semNenhum')}
                    </Typography>
                    <MDButton
                        variant="gradient"
                        color="info"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setCreating(true)}
                    >
                        {t('webhooks.adicionar')}
                    </MDButton>
                </Box>
            ) : (
                list.map((item) => (
                    <WebhookRow
                        key={item.id}
                        item={item}
                        onEdit={() => setEdit(item)}
                        onDelete={() => remove(item)}
                    />
                ))
            )}

            {(creating || edit) && (
                <WebhookDialog
                    edit={edit}
                    onClose={() => { setCreating(false); setEdit(null); }}
                    onSaved={() => { setEdit(null); load(); }}
                />
            )}
        </Box>
    );
};
